import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import hcinfoLogo from '../../../assets/images/hcinfo.png';
import { getNumberComma, num2han } from '../../../utils/numberUtils';
import dayjs from 'dayjs';
import _ from 'lodash';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      align: 'center',
    },
  },
});

const OrderSheetPreview = ({
  previewRef,
  project,
  orderSheet,
  orderDetails,
}) => {
  const classes = useStyles();
  const [totalPrice, setTotalPrice] = useState(0);
  const [withoutTax, setWithoutTax] = useState(0);
  const [taxPrice, setTaxPrice] = useState(0);

  useEffect(() => {
    if (orderDetails) {
      // round 처리함.
      //부가세가 제외된 원금 = 합계액 * 100 / 110
      const price = _.sumBy(orderDetails, 'buyVal');
      const tax = Math.round(price * 0.1);
      const total = Math.round(tax + price);

      setWithoutTax(price); //부가세 없는 금액
      setTotalPrice(total);
      setTaxPrice(tax);
    }
  }, [orderDetails]);

  return (
    <Box
      ref={previewRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: (theme) => theme.palette.divider,
        p: 2,
      }}
    >
      <Stack
        id="hcinfo"
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={2}
      >
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          <img src={hcinfoLogo} width="180" height="48" alt="hcinfo" />
        </Box>
        <Box>
          <Typography variant="body2" component="div">
            서울시 성동구 성수일로8길 5 서울숲SK V1타워 A동1805호
          </Typography>
          <Typography variant="body2" component="div">
            Tel. 070-4610-5540~8 Fax . 070-4009-6261
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          borderTop: '3px dotted',
          borderColor: (theme) => theme.palette.divider,
          py: 1,
        }}
      >
        <Typography variant="h4" component="div">
          발주서
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          py: 1,
        }}
      >
        <Typography variant="h6" component="div">
          수신 : {orderDetails[0].sellPartnerNm} 귀중
        </Typography>
        <Typography variant="body2" component="div">
          귀사에 아래와 같이 발주 합니다.
        </Typography>
      </Box>

      <Table id="orderSheetInfo" className={classes.table}>
        <TableBody>
          <TableRow>
            <TableCell width="20%">담당</TableCell>
            <TableCell width="30%">{orderSheet?.sheetUsrNm}</TableCell>
            <TableCell width="20%">발주번호</TableCell>
            <TableCell width="30%">{orderSheet?.orderSheetNo}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell width="20%">연락처</TableCell>
            <TableCell width="30%">{orderSheet?.sheetUsrTel}</TableCell>
            <TableCell width="20%">발주일자</TableCell>
            <TableCell width="30%">
              {dayjs(orderSheet?.sheetCrtDate).format('YYYY-MM-DD')}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell width="20%">사업명</TableCell>
            <TableCell width="30%">{orderSheet?.sheetCstNm}</TableCell>
            <TableCell width="50%" rowSpan={3} colSpan={2}>
              <Typography variant="subtitle2" component="div">
                주식회사 에이치씨인포
              </Typography>
              <Typography variant="subtitle2" component="div">
                대표이사 김경운{' '}
              </Typography>
              <Typography variant="subtitle2" component="div">
                사업자 번호 : 215-87-68879
              </Typography>
              <Typography variant="subtitle2" component="div">
                서울시 성동구 성수일로8길 5 서울숲 SK V1 타워 A동1208호
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell width="20%">결제조건</TableCell>
            <TableCell width="30%">{orderSheet?.sheetPayCond}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell width="20%">납기</TableCell>
            <TableCell width="30%">{orderSheet?.sheetPeriod}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          p: 2,
          my: 1,
          backgroundColor: 'yellow',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" component="div">
            발주금액 : (부가세 포함)一金 {num2han(totalPrice)} 원整
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" component="div">
            [단위 : 원 ]
          </Typography>
        </Box>
      </Box>

      <TableContainer
        id="orderDetailInfo"
        size="small"
        className={classes.table}
        sx={{ width: '100%', display: 'table', tableLayout: 'fixed' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>항번</TableCell>
              <TableCell>제품명</TableCell>
              <TableCell>상세내역</TableCell>
              <TableCell>수량</TableCell>
              <TableCell>단가</TableCell>
              <TableCell>합계</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderDetails &&
              orderDetails.map((cost, index) => (
                <TableRow key={index}>
                  <TableCell width="10%">
                    {orderDetails.length - index}
                  </TableCell>
                  <TableCell width="40%">{cost.productNm}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{ whiteSpace: 'pre-line' }}
                      variant="body2"
                      component="div"
                    >
                      {cost?.productDesc}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width="10%">
                    {cost.expCnt}
                  </TableCell>
                  <TableCell align="right" width="10%">
                    {getNumberComma(cost.priceVal)}
                  </TableCell>
                  <TableCell align="right" width="10%">
                    {getNumberComma(cost.buyVal)}
                  </TableCell>
                </TableRow>
              ))}
            <TableRow>
              <TableCell colSpan={3}>합계금액(부가세 별도)</TableCell>
              <TableCell colSpan={3} align="right">
                {getNumberComma(withoutTax)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3}>부가세</TableCell>
              <TableCell colSpan={3} align="right">
                {getNumberComma(taxPrice)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3}>총합계금액(부가세 포함)</TableCell>
              <TableCell colSpan={3} align="right">
                {getNumberComma(totalPrice)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          border: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          my: 2,
          p: 1,
        }}
      >
        <Typography variant="subtitle2" component="div" sx={{ my: 1 }}>
          # 기타사항
        </Typography>
        <Typography
          sx={{ whiteSpace: 'pre-line' }}
          variant="body2"
          component="div"
        >
          {orderSheet?.sheetDesc}
        </Typography>
      </Box>
    </Box>
  );
};

export default OrderSheetPreview;
